/**
 * The main chart: candles, volume subplot, overlays, S/R zones, signal levels.
 *
 * Everything the spec asked a TradingView-style chart to do:
 *   * candlestick + volume in its own pane;
 *   * timeframe tabs that also switch which horizon the Signal Panel shows;
 *   * toggleable EMA / Bollinger / zones / Fibonacci overlays;
 *   * a buy/sell marker on the exact bar the signal fired;
 *   * horizontal entry / stop / target lines with price labels.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { LineStyle } from 'lightweight-charts'
import type { IPriceLine } from 'lightweight-charts'

import type { Candle, Signal, StructureResponse } from '@/api/types'

/** What Lightweight Charts hands an `autoscaleInfoProvider`. */
type AutoscaleProvider = () => { priceRange: { minValue: number; maxValue: number } | null } | null
import {
  useCandles, useOverlays, useSignals, useStructure, useSymbols, useWatchlists,
} from '@/hooks/useQueries'
import { useAppStore } from '@/store/useAppStore'
import { COLORS, TIMEFRAMES } from '@/theme/chart'
import { VOLUME_COLORS, clearLines, upsertLine, useChart } from './useChart'
import type { ChartHandles } from './useChart'
import { OverlayToggles } from './OverlayToggles'

interface Props {
  ticker: string | null
  liveCandle: Candle | null
  /** Last traded price off the socket, the same value the watchlist row shows. */
  livePrice?: number
}

export function ChartPanel({ ticker, liveCandle, livePrice }: Props) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null)
  const handles = useChart(container)
  const priceLinesRef = useRef<IPriceLine[]>([])
  // Which (symbol, timeframe) the current view was fitted to.
  const viewKeyRef = useRef<string | null>(null)

  const timeframe = useAppStore((s) => s.timeframe)
  const setTimeframe = useAppStore((s) => s.setTimeframe)
  const showEma = useAppStore((s) => s.showEma)
  const showBollinger = useAppStore((s) => s.showBollinger)
  const showZones = useAppStore((s) => s.showZones)
  const showFib = useAppStore((s) => s.showFib)
  const showSignalLines = useAppStore((s) => s.showSignalLines)
  const showVolume = useAppStore((s) => s.showVolume)

  // Vietnamese equities carry no 4h series: a HOSE session is 4.25 hours, so
  // a 4h bar would be a misaligned near-copy of the daily one and the seed
  // leaves it out. The whole `swing` horizon therefore has no trigger
  // timeframe for these symbols, and every evaluation stops at stage 0. The
  // tab is disabled rather than hidden so the reason is visible.
  const { data: allSymbols } = useSymbols()
  const { data: watchlists } = useWatchlists()
  const symbol = allSymbols?.find((s) => s.ticker === ticker)

  // What the header quotes as "the price". The last close of the selected
  // timeframe is the wrong answer: a 15m chart lags the tape by up to
  // fifteen minutes, so the header read 252,600 while the watchlist row for
  // the same symbol read 249,400 -- both correct, and impossible to
  // reconcile by looking. The socket price first, then the same REST field
  // the watchlist uses, and only then the last bar.
  const watchlistRow = useMemo(
    () =>
      (watchlists ?? [])
        .flatMap((watchlist) => watchlist.items)
        .find((item) => item.symbol.ticker === ticker),
    [watchlists, ticker],
  )

  const quotedPrice = livePrice ?? watchlistRow?.last_price ?? undefined

  // The same 24h figure the watchlist row shows. The alternative -- the last
  // bar's own open-to-close move -- answers a different question and made the
  // header disagree with the row for the same symbol.
  const quotedChange = watchlistRow?.change_pct_24h ?? undefined
  const assetClass = symbol?.asset_class
  // Vietnamese equities quote whole dong. Without this the chart rendered
  // "252,600.00" beside a watchlist row reading "253,300" -- two decimals of
  // noise, and two different-looking numbers for one price.
  const priceDecimals = symbol?.price_decimals ?? undefined
  const unavailable = useMemo(
    () => (assetClass === 'stock_vn' ? new Set(['4h']) : new Set<string>()),
    [assetClass],
  )

  // Resolved during render, not in the effect below. The effect only runs
  // after the first paint, so the queries fired once against the timeframe
  // the symbol does not have -- a 404 for /structure/VIC?timeframe=4h on
  // every switch from a crypto chart to a Vietnamese one.
  const activeTimeframe = unavailable.has(timeframe) ? '1d' : timeframe

  useEffect(() => {
    if (unavailable.has(timeframe)) setTimeframe('1d' as never)
  }, [unavailable, timeframe, setTimeframe])

  // The price axis has to match the instrument, not the chart library's
  // default of two decimals. Its own effect because it depends only on the
  // symbol, and burying it in the signal-overlay effect would leave it
  // unapplied whenever a signal happens to be showing.
  useEffect(() => {
    if (!handles) return
    const digits = priceDecimals ?? 2
    handles.candles.applyOptions({
      priceFormat: { type: 'price', precision: digits, minMove: 1 / 10 ** digits },
    })
  }, [handles, priceDecimals])

  const candlesQuery = useCandles(ticker, activeTimeframe)
  const overlaysQuery = useOverlays(ticker, activeTimeframe, showEma || showBollinger)
  const structureQuery = useStructure(ticker, activeTimeframe, showZones || showFib)
  const signalsQuery = useSignals(ticker, activeTimeframe)

  const signal = signalsQuery.data?.[0] ?? null
  const lastPrice =
    liveCandle?.close ?? candlesQuery.data?.candles.at(-1)?.close ?? null

  // ---- candles + volume ----
  useEffect(() => {
    if (!handles || !candlesQuery.data) return
    const candles = candlesQuery.data.candles
    handles.candles.setData(
      candles.map((c) => ({
        time: c.time as never,
        open: c.open,
        high: c.high,
        low: c.low,
        close: c.close,
      })),
    )
    handles.volume.setData(
      candles.map((c) => ({
        time: c.time as never,
        value: c.volume,
        color: c.close >= c.open ? VOLUME_COLORS.up : VOLUME_COLORS.down,
      })),
    )

    // Only reset the view when the user actually switched instrument or
    // timeframe. Doing it on every refetch would yank the chart back while
    // they are reading a zoomed-in section.
    const key = `${ticker}:${activeTimeframe}`
    if (viewKeyRef.current !== key) {
      viewKeyRef.current = key
      resetView(handles)
    }

    if (import.meta.env.DEV) {
      // Testing seam: the price scale is painted on a canvas, so the smoke
      // test has no other way to check that the visible range really covers
      // the current price after switching symbols.
      ;(window as unknown as { __chart?: unknown }).__chart = {
        priceToCoordinate: (price: number) => handles.candles.priceToCoordinate(price),
        lastClose: candles.at(-1)?.close ?? null,
      }
    }
  }, [handles, candlesQuery.data, ticker, activeTimeframe])

  // ---- live candle from the socket ----
  useEffect(() => {
    if (!handles || !liveCandle) return

    // The socket candle starts from the first tick after the page loaded, so
    // its `open` is that tick's price, not the bucket's real open. Merged
    // with the fetched bar for the same bucket the body stays honest;
    // without it the newest candle collapses to a doji the moment a tick
    // arrives, and its high and low forget everything before that tick.
    const fetched = candlesQuery.data?.candles.at(-1)
    const base = fetched?.time === liveCandle.time ? fetched : null
    const merged = {
      time: liveCandle.time,
      open: base?.open ?? liveCandle.open,
      high: Math.max(base?.high ?? liveCandle.high, liveCandle.high),
      low: Math.min(base?.low ?? liveCandle.low, liveCandle.low),
      close: liveCandle.close,
      volume: Math.max(base?.volume ?? 0, liveCandle.volume),
    }

    handles.candles.update({
      time: merged.time as never,
      open: merged.open,
      high: merged.high,
      low: merged.low,
      close: merged.close,
    })
    handles.volume.update({
      time: merged.time as never,
      value: merged.volume,
      color: merged.close >= merged.open ? VOLUME_COLORS.up : VOLUME_COLORS.down,
    })
  }, [handles, liveCandle, candlesQuery.data])

  // ---- volume pane visibility ----
  //
  // Yahoo reports zero volume for FX pairs, so an always-on pane would burn
  // 90px on a flat line. Hide it when the feed has nothing to show -- the
  // same fact the engine encodes by having `vol_anomaly` abstain.
  const hasVolume = useMemo(
    () => (candlesQuery.data?.candles ?? []).some((candle) => candle.volume > 0),
    [candlesQuery.data],
  )

  useEffect(() => {
    if (!handles) return
    const visible = showVolume && hasVolume
    handles.volume.applyOptions({ visible })
    handles.chart.panes()[1]?.setHeight(visible ? 90 : 1)
  }, [handles, showVolume, hasVolume])

  // ---- indicator overlays ----
  useEffect(() => {
    if (!handles) return
    const wanted = new Set<string>()
    for (const series of overlaysQuery.data?.series ?? []) {
      const isEma = series.key.startsWith('ema')
      const isBb = series.key.startsWith('bb')
      if ((isEma && !showEma) || (isBb && !showBollinger)) continue
      wanted.add(series.key)
      upsertLine(handles, series.key, series.points, series.color ?? COLORS.accent)
    }
    clearLines(handles, wanted)
  }, [handles, overlaysQuery.data, showEma, showBollinger])

  // ---- support/resistance zones ----
  useEffect(() => {
    if (!handles) return
    if (!showZones) {
      handles.zones.setZones([])
      return
    }
    const all = structureQuery.data?.zones ?? []
    const reference = lastPrice ?? all[0]?.price_high ?? 0

    // A level 40% away from price is history, not a trading level, and on a
    // daily chart there are enough of them to bury the candles.
    const near = all
      .filter((zone) => {
        if (!reference) return true
        const distance = Math.min(
          Math.abs(zone.price_low - reference),
          Math.abs(zone.price_high - reference),
        )
        return distance / reference <= 0.25
      })
      .sort((a, b) => b.strength - a.strength)
      .slice(0, 8)

    handles.zones.setZones(near)
  }, [handles, structureQuery.data, showZones, lastPrice])

  // ---- markers + entry/SL/TP lines ----
  useEffect(() => {
    if (!handles) return

    for (const line of priceLinesRef.current) handles.candles.removePriceLine(line)
    priceLinesRef.current = []

    if (!signal || !showSignalLines) {
      handles.markers.setMarkers([])
      return
    }

    const isLong = signal.side === 'long'
    const barTime = Math.floor(new Date(signal.bar_ts).getTime() / 1000)

    handles.markers.setMarkers([
      {
        time: barTime as never,
        position: isLong ? 'belowBar' : 'aboveBar',
        color: isLong ? COLORS.up : COLORS.down,
        shape: isLong ? 'arrowUp' : 'arrowDown',
        text: `${isLong ? 'MUA' : 'BÁN'} ${signal.confidence.toFixed(0)}`,
      },
    ])

    const add = (price: number, color: string, title: string, style: LineStyle) => {
      priceLinesRef.current.push(
        handles.candles.createPriceLine({
          price,
          color,
          lineWidth: 1,
          lineStyle: style,
          axisLabelVisible: true,
          title,
        }),
      )
    }

    add(signal.entry_high, COLORS.entry, 'Entry', LineStyle.Solid)
    add(signal.entry_low, COLORS.entry, '', LineStyle.Dotted)
    add(signal.stop_loss, COLORS.stop, 'SL', LineStyle.Dashed)
    for (const target of signal.targets) {
      add(target.price, COLORS.target, `TP${target.idx}`, LineStyle.Dashed)
    }
  }, [handles, signal, showSignalLines])

  // ---- keep the signal levels inside the visible price range ----
  //
  // Price lines do not participate in autoscaling, so a target 8% above the
  // last candle simply renders off-screen -- the user sees an entry and a
  // stop and has to take the target on trust. Widening the autoscale range
  // to cover the whole idea is what makes the levels readable.
  useEffect(() => {
    if (!handles) return

    if (!signal || !showSignalLines) {
      // Passing `undefined` does not reliably clear a previously applied
      // option, so install a pass-through provider instead. Leaving a stale
      // one in place would stretch the next symbol's price range to cover
      // the previous symbol's targets.
      handles.candles.applyOptions({
        autoscaleInfoProvider: (original: AutoscaleProvider) => original(),
      })
      return
    }

    const levels = [
      signal.entry_low, signal.entry_high, signal.stop_loss,
      ...signal.targets.map((target) => target.price),
    ]

    handles.candles.applyOptions({
      autoscaleInfoProvider: (original: AutoscaleProvider) => {
        const info = original()
        if (!info?.priceRange) return info
        return {
          ...info,
          priceRange: {
            minValue: Math.min(info.priceRange.minValue, ...levels),
            maxValue: Math.max(info.priceRange.maxValue, ...levels),
          },
        }
      },
    })
  }, [handles, signal, showSignalLines])

  // ---- Fibonacci ----
  useEffect(() => {
    if (!handles) return
    const existing = priceLinesRef.current
    if (!showFib || !structureQuery.data?.fib.length) return

    const fibLines = structureQuery.data.fib
      .filter((level) => level.kind === 'retracement')
      .map((level) =>
        handles.candles.createPriceLine({
          price: level.price,
          color: COLORS.fib,
          lineWidth: 1,
          lineStyle: LineStyle.LargeDashed,
          axisLabelVisible: true,
          title: `Fib ${level.ratio}`,
        }),
      )
    priceLinesRef.current = [...existing, ...fibLines]

    return () => {
      for (const line of fibLines) handles.candles.removePriceLine(line)
      priceLinesRef.current = priceLinesRef.current.filter((l) => !fibLines.includes(l))
    }
  }, [handles, structureQuery.data, showFib])

  const lastCandle = liveCandle ?? candlesQuery.data?.candles.at(-1) ?? null

  return (
    <section className="flex min-h-0 flex-1 flex-col bg-panel">
      <ChartHeader
        ticker={ticker}
        timeframe={activeTimeframe}
        onTimeframe={setTimeframe}
        unavailable={unavailable}
        lastCandle={lastCandle}
        quotedPrice={quotedPrice}
        quotedChange={quotedChange}
        priceDecimals={priceDecimals}
        assetClass={assetClass}
        structure={structureQuery.data ?? null}
        signal={signal}
      />
      <OverlayToggles />
      <div className="relative min-h-0 flex-1">
        <div ref={setContainer} className="absolute inset-0" />
        {!ticker && (
          <div className="absolute inset-0 grid place-items-center text-ink-dim">
            Chọn một mã ở watchlist bên trái
          </div>
        )}
        {ticker && candlesQuery.isLoading && (
          <div className="absolute inset-0 grid place-items-center text-ink-dim">Đang tải nến…</div>
        )}
        {ticker && candlesQuery.data?.candles.length === 0 && (
          <div className="absolute inset-0 grid place-items-center text-ink-dim">
            Chưa có dữ liệu nến cho khung {timeframe}
          </div>
        )}
      </div>
    </section>
  )
}

function ChartHeader({
  ticker, timeframe, onTimeframe, unavailable, lastCandle, quotedPrice, quotedChange,
  priceDecimals, assetClass, structure, signal,
}: {
  ticker: string | null
  timeframe: string
  onTimeframe: (tf: never) => void
  unavailable: Set<string>
  assetClass?: string
  quotedPrice?: number
  quotedChange?: number
  priceDecimals?: number
  lastCandle: Candle | null
  structure: StructureResponse | null
  signal: Signal | null
}) {
  const change = useMemo(() => {
    if (quotedChange !== undefined) return quotedChange
    if (!lastCandle || !lastCandle.open) return null
    return ((lastCandle.close - lastCandle.open) / lastCandle.open) * 100
  }, [quotedChange, lastCandle])

  return (
    <header className="flex flex-wrap items-center gap-3 border-b border-border px-3 py-2">
      <div className="flex items-baseline gap-2">
        <h2 className="text-sm font-semibold tracking-wide">{ticker ?? '—'}</h2>
        {lastCandle && (
          <>
            <span className="font-mono text-sm">
              {formatPrice(quotedPrice ?? lastCandle.close, priceDecimals)}
            </span>
            {change !== null && (
              <span className={`font-mono text-2xs ${change >= 0 ? 'text-up' : 'text-down'}`}>
                {change >= 0 ? '+' : ''}{change.toFixed(2)}%
              </span>
            )}
            {!lastCandle.is_final && (
              <span
                className="rounded bg-panel-hover px-1.5 py-0.5 text-2xs text-ink-dim"
                title="Nến đang chạy — engine không dùng nến này để ra tín hiệu"
              >
                đang chạy
              </span>
            )}
            {assetClass === 'gold' && (
              // Gold's price arrives roughly every three minutes, not every
              // second like crypto: its vendor sells a fixed number of calls
              // per day and the allowance cannot buy a faster quote. Said
              // out loud here because a number that sits still for minutes
              // looks broken, and a correct system that looks broken gets
              // debugged as though it were.
              <span
                className="rounded bg-panel-hover px-1.5 py-0.5 text-2xs text-ink-dim"
                title="Giá vàng cập nhật mỗi ~3 phút (giới hạn số lần gọi/ngày của nguồn dữ liệu). Đứng yên vài phút là bình thường, không phải lỗi. Nến 4h/1d — khung mà module này nhắm tới — không bị ảnh hưởng."
              >
                ~3 phút/lần
              </span>
            )}
          </>
        )}
      </div>

      <div className="flex overflow-hidden rounded border border-border">
        {TIMEFRAMES.map((tf) => {
          const off = unavailable.has(tf)
          return (
            <button
              key={tf}
              disabled={off}
              title={off ? 'Cổ phiếu VN không có khung 4h — phiên HOSE chỉ 4,25 giờ' : undefined}
              onClick={() => onTimeframe(tf as never)}
              className={`px-2.5 py-1 text-2xs transition-colors ${
                off
                  ? 'cursor-not-allowed bg-panel-alt text-ink-dim/40 line-through'
                  : tf === timeframe
                    ? 'bg-accent text-white'
                    : 'bg-panel-alt text-ink-dim hover:bg-panel-hover hover:text-ink'
              }`}
            >
              {tf}
            </button>
          )
        })}
      </div>

      {structure && (
        <span className="rounded bg-panel-alt px-2 py-0.5 text-2xs text-ink-dim">
          {regimeLabel(structure.regime)} · {structure.zones.length} vùng
        </span>
      )}

      {signal && (
        <span
          className={`ml-auto rounded px-2 py-0.5 text-2xs font-medium ${
            signal.side === 'long' ? 'bg-up/20 text-up' : 'bg-down/20 text-down'
          }`}
        >
          {signal.side === 'long' ? 'MUA' : 'BÁN'} · tin cậy {signal.confidence.toFixed(0)} ·
          R:R {signal.rr_ratio.toFixed(2)}
        </span>
      )}
    </header>
  )
}

/**
 * Put the chart back to "fit everything, autoscale on".
 *
 * Dragging the price axis switches that scale to manual mode, and manual
 * mode survives a `setData`. Without this, selecting XRP at 1.44 right after
 * looking at BTC leaves the price scale showing 52,000-100,000 and an empty
 * chart -- the data is there, it is just far off-screen.
 */
function resetView(handles: ChartHandles): void {
  handles.chart.priceScale('right').applyOptions({ autoScale: true })
  handles.volume.priceScale().applyOptions({ autoScale: true })
  handles.chart.timeScale().fitContent()
}

export function formatPrice(value: number, decimals?: number): string {
  // Vietnamese equities quote whole dong (72,800), so forcing two decimals
  // would render every price as "72,800.00" -- noise on every row.
  const digits = decimals ?? (value >= 1000 ? 2 : value >= 1 ? 4 : 6)
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })
}

export function regimeLabel(regime: string): string {
  const labels: Record<string, string> = {
    trend_up: 'Xu hướng tăng',
    trend_down: 'Xu hướng giảm',
    range: 'Đi ngang',
    squeeze: 'Nén',
    volatile: 'Biến động mạnh',
  }
  return labels[regime] ?? regime
}
